// Instagram Insights Required Banner - shown when creator tries to submit
// Instagram Stories but hasn't linked their Instagram Insights

import type { ContentRequirementSocialAccount } from "@/lib/queries/content";
import { Link, useParams } from "@tanstack/react-router";
import { ExternalLink, Info } from "lucide-react";

/**
 * Check if Instagram Insights are not linked for a set of social accounts.
 * Returns true when at least one Instagram account lacks analytics API access.
 */
export function isInstagramInsightsNotLinked(
  socialAccounts: ContentRequirementSocialAccount[] | null | undefined,
): boolean {
  if (!socialAccounts) return false;
  return socialAccounts.some(
    (a) =>
      a.networkIdentifier?.toLowerCase() === "instagram" &&
      !a.hasAnalyticsApiAccess,
  );
}

/**
 * Return the usernames of Instagram accounts that lack analytics API access.
 */
export function getUnlinkedInstagramUsernames(
  socialAccounts: ContentRequirementSocialAccount[] | null | undefined,
): string[] {
  if (!socialAccounts) return [];
  return socialAccounts
    .filter(
      (a) =>
        a.networkIdentifier?.toLowerCase() === "instagram" &&
        !a.hasAnalyticsApiAccess &&
        a.username,
    )
    .map((a) => a.username as string);
}

const HELP_CENTER_URL =
  "https://help.aspireiq.com/en/articles/4508836-troubleshooting-instagram-insights";

interface InstagramInsightsBannerProps {
  accountUsernames?: string[];
}

export function InstagramInsightsBanner({
  accountUsernames,
}: InstagramInsightsBannerProps = {}) {
  const { orgSlug } = useParams({ strict: false }) as { orgSlug?: string };
  const hasAccounts = accountUsernames && accountUsernames.length > 0;
  return (
    <div className="flex gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
      <Info className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="text-sm text-amber-800 dark:text-amber-200">
        <strong className="block mb-1">
          Instagram Insights must be linked to submit stories.
        </strong>
        {hasAccounts && (
          <p className="mb-1 text-amber-700 dark:text-amber-300">
            Account{accountUsernames.length > 1 ? "s" : ""} requiring Insights:{" "}
            {accountUsernames.map((u) => `@${u}`).join(", ")}
          </p>
        )}
        <p className="mb-2">
          {orgSlug ? (
            <>
              Connect your Instagram account under{" "}
              <Link
                to="/o/$orgSlug/settings"
                params={{ orgSlug }}
                search={{ tab: "profiles" }}
                className="underline font-medium"
              >
                Profile Settings
              </Link>{" "}
              to link Insights, or learn more in our{" "}
            </>
          ) : (
            <>Learn more about connecting Instagram Insights in our </>
          )}
          <a
            href={HELP_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-medium inline-flex items-center gap-1"
          >
            Help Center
            <ExternalLink className="h-3 w-3" />
          </a>
          .
        </p>
      </div>
    </div>
  );
}
